/**
 * Merchant Pro RBAC Sub-phase C3 — Staff Roles page.
 *
 * Route: /owner/staff/roles
 * Access: capability `staff.view` (Brand Owner + Supervisor — wait,
 *   Supervisor does NOT have staff.* in C1 seed; so effectively
 *   Brand Owner + super_admin). Route gate handles redirect.
 *
 * Responsibilities:
 *   • List system_defaults + custom roles for the actor's brand.
 *   • Allow Clone on every row (including system_defaults).
 *   • Allow Create from scratch.
 *   • Allow Edit on custom roles only (system_defaults are read-only).
 *   • Allow Deactivate on custom roles only · shows count of users
 *     who will be auto-reverted before confirming.
 *
 * Tier-1 invariant: no role-equality JSX. All UX gating uses
 * `<RequirePermission cap="...">` from `lib/capabilities`.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Copy, Pencil, ShieldOff, RefreshCw, Lock, Users,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import PermissionMatrixEditor from '@/components/owner/PermissionMatrixEditor';
import RequirePermission from '@/lib/capabilities';

const BASE_ROLE_LABELS = {
  brand_owner: 'Brand Owner',
  cashier:     'Cashier',
  game_master: 'Game Master',
};

const ROLES_QK = ['rbac', 'staff', 'roles'];
const CATALOG_QK = ['rbac', 'staff', 'capabilities'];

function Pill({ children, tone = 'gray' }) {
  const palette = {
    gray:  'bg-white/5 text-white/70 border-white/10',
    gold:  'bg-gold/10 text-gold border-gold/30',
    red:   'bg-red-500/10 text-red-300 border-red-500/30',
    green: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-semibold border ${palette}`}>
      {children}
    </span>
  );
}

function CloneDialog({ open, source, onClose, onConfirm, busy }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  if (!open) return null;
  return (
    <div
      data-testid="clone-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#15151B] p-6">
        <h3 className="text-lg font-semibold text-white">
          Clone {source?.name}
        </h3>
        <p className="text-sm text-white/55 mt-1">
          Creates a new editable custom role with the same capability set.
          Naming rule: 2–50 chars, letters / numbers / spaces / dashes / underscores.
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New role name (e.g. Senior Cashier)"
          data-testid="clone-name-input"
          className="mt-4 w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={2}
          data-testid="clone-description-input"
          className="mt-2 w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40"
        />
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10"
            data-testid="clone-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ name: name.trim(), description: description.trim() })}
            disabled={busy || name.trim().length < 2}
            data-testid="clone-confirm"
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 disabled:opacity-50"
          >
            {busy ? 'Cloning…' : 'Create clone'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeactivateDialog({ open, role, onClose, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div
      data-testid="deactivate-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-[#1B0F0F] p-6">
        <h3 className="text-lg font-semibold text-red-200 flex items-center gap-2">
          <ShieldOff size={18} /> Deactivate &quot;{role?.name}&quot;
        </h3>
        <p className="text-sm text-white/70 mt-3">
          This will soft-delete the role and{' '}
          <strong data-testid="deactivate-revert-count" className="text-red-300">
            {role?.assigned_user_count ?? 0}
          </strong>
          {' '}user{(role?.assigned_user_count ?? 0) === 1 ? '' : 's'} currently assigned to it
          will be auto-reverted to their base-role system default. This action can be undone
          by re-creating an equivalent role from scratch — system defaults are unaffected.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="deactivate-cancel"
            className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="deactivate-confirm"
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-50"
          >
            {busy ? 'Deactivating…' : 'Deactivate role'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditDrawer({ open, role, catalog, allRoles, onClose, onSave, busy }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capabilities, setCapabilities] = useState([]);

  // hydrate on open
  const opened = open && role;
  useMemo(() => {
    if (opened) {
      setName(role.name);
      setDescription(role.description || '');
      setCapabilities([...(role.capabilities || [])]);
    }
  }, [opened, role]);

  if (!open || !role) return null;
  const isSystemDefault = !!role.is_system_default;
  const baseDefault = (allRoles || []).find(
    (r) => r.is_system_default && r.base_role_enum === role.base_role_enum,
  );

  return (
    <div
      data-testid="role-editor-drawer"
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70"
    >
      <div className="w-full max-w-3xl bg-[#15151B] border-l border-white/10 h-full flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              {role.name}
              {isSystemDefault && <Pill tone="gold"><Lock size={10} /> System</Pill>}
            </h3>
            <p className="text-xs text-white/55 mt-1">
              Base role · <span className="text-white/80">{BASE_ROLE_LABELS[role.base_role_enum] || role.base_role_enum}</span>
              {' · '}{role.assigned_user_count} assigned user{role.assigned_user_count === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="role-editor-close"
            className="px-3 py-1.5 text-sm text-white/70 hover:bg-white/5 rounded-lg border border-white/10"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/55 font-semibold mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              disabled={isSystemDefault}
              onChange={(e) => setName(e.target.value)}
              data-testid="role-editor-name"
              className="w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/55 font-semibold mb-1">
              Description
            </label>
            <textarea
              rows={2}
              value={description}
              disabled={isSystemDefault}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="role-editor-description"
              className="w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40 disabled:opacity-50"
            />
          </div>

          <PermissionMatrixEditor
            catalog={catalog}
            baseRoleSystemDefaultCaps={baseDefault?.capabilities || []}
            selectedCapabilities={capabilities}
            onChange={setCapabilities}
            disabled={isSystemDefault}
          />
        </div>

        {!isSystemDefault && (
          <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave({ name: name.trim(), description: description.trim(), capabilities })}
              disabled={busy || name.trim().length < 2}
              data-testid="role-editor-save"
              className="px-4 py-2 text-sm font-semibold rounded-xl bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StaffRolesPage() {
  const { token, user } = useAuthStore();
  const qc = useQueryClient();
  // PR-Audit-02B G-08 (2026-06-19) — SA cross-brand view. When the
  // actor is super_admin with no JWT-bound brand_id, expose a brand
  // selector so they can read another brand's staff roles. brand_owner
  // sees no selector (their brand is JWT-pinned).
  const isSuperAdmin = user?.role === 'super_admin';
  const needsBrandSelector = isSuperAdmin && !user?.brand_id;
  const [saBrandId, setSaBrandId] = useState(null);
  const { data: saBrands = [] } = useQuery({
    queryKey: ['admin', 'brands', 'staff-roles-selector'],
    queryFn: () => api.adminBrands(token),
    enabled: !!token && needsBrandSelector,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (needsBrandSelector && !saBrandId && saBrands.length > 0) {
      // saBrands may be wrapped as { items: [...] } or a plain array;
      // tolerate both shapes since /api/admin/brands has multiple
      // response shapes across endpoints.
      const list = Array.isArray(saBrands) ? saBrands : saBrands.items || [];
      if (list.length > 0) setSaBrandId(list[0].id);
    }
  }, [needsBrandSelector, saBrandId, saBrands]);
  const effectiveBrandId = needsBrandSelector ? saBrandId : null;

  const { data: catalog } = useQuery({
    queryKey: CATALOG_QK,
    queryFn: () => api.ownerStaffCapabilities(token),
    enabled: !!token,
    staleTime: 60 * 60 * 1000,
  });
  const { data: rolesData, isLoading } = useQuery({
    queryKey: [...ROLES_QK, effectiveBrandId || 'self'],
    queryFn: () => api.ownerStaffRoles(token, false, effectiveBrandId),
    enabled: !!token && (!needsBrandSelector || !!effectiveBrandId),
  });
  const roles = useMemo(() => rolesData?.items || [], [rolesData]);

  // Dialog states
  const [cloneTarget, setCloneTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  // PR-Audit-02B G-07 (2026-06-19) — "Create role" picker. Operator-
  // ratified path is "create from system default + rename". This is
  // the existing Clone endpoint under a more discoverable label — no
  // new role-builder/wizard. The picker just routes the user to the
  // right system_default before opening the existing CloneDialog.
  const [createPickerOpen, setCreatePickerOpen] = useState(false);
  const systemDefaults = useMemo(
    () => roles.filter((r) => r.is_system_default && r.is_active !== false),
    [roles],
  );

  const cloneM = useMutation({
    mutationFn: ({ roleId, body }) => api.ownerCloneStaffRole(token, roleId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ROLES_QK }); setCloneTarget(null); },
  });
  const editM = useMutation({
    mutationFn: ({ roleId, body }) => api.ownerPatchStaffRole(token, roleId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ROLES_QK }); setEditTarget(null); },
  });
  const deactivateM = useMutation({
    mutationFn: (roleId) => api.ownerDeactivateStaffRole(token, roleId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ROLES_QK }); setDeactivateTarget(null); },
  });

  return (
    <div className="space-y-6" data-testid="staff-roles-page">
      {needsBrandSelector ? (
        <div
          className="rounded-2xl border border-gold/30 bg-gold/5 p-4 flex items-center justify-between gap-3 flex-wrap"
          data-testid="staff-roles-sa-brand-selector"
        >
          <div>
            <div className="text-xs uppercase tracking-widest text-gold/85 font-semibold">
              Super Admin · Brand Context
            </div>
            <div className="text-sm text-white/70 mt-0.5">
              Switch the brand whose staff roles you want to inspect. Mutations
              are still scoped to the brand owner&apos;s session — Super Admin
              has read-only reach here.
            </div>
          </div>
          <select
            value={saBrandId || ''}
            onChange={(e) => setSaBrandId(e.target.value || null)}
            data-testid="staff-roles-sa-brand-select"
            className="px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[220px]"
          >
            {(Array.isArray(saBrands) ? saBrands : saBrands.items || []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.slug})
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Staff Roles</h1>
          <p className="text-sm text-white/55 mt-1">
            System defaults can be cloned but not edited or deactivated. Custom roles can be
            edited or deactivated at any time — users will be auto-reverted to their base
            role&apos;s system default.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RequirePermission cap="staff.clone_role">
            <button
              type="button"
              onClick={() => setCreatePickerOpen(true)}
              data-testid="staff-roles-create"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25"
            >
              <Plus size={14} /> Create role
            </button>
          </RequirePermission>
          <button
            type="button"
            onClick={() => qc.invalidateQueries({ queryKey: ROLES_QK })}
            data-testid="staff-roles-refresh"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-white/10 hover:bg-white/5 text-white/80"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Roles list */}
      <div className="rounded-2xl border border-white/10 bg-[#15151B] overflow-hidden">
        <table className="w-full text-sm" data-testid="staff-roles-table">
          <thead className="bg-[#0E0E14] text-xs uppercase tracking-widest text-white/55">
            <tr>
              <th className="px-5 py-3 text-left font-semibold">Role</th>
              <th className="px-5 py-3 text-left font-semibold">Base</th>
              <th className="px-5 py-3 text-left font-semibold">Capabilities</th>
              <th className="px-5 py-3 text-left font-semibold"><Users size={12} className="inline" /> Users</th>
              <th className="px-5 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && (
              <tr><td colSpan={5} className="text-center py-8 text-white/55">Loading…</td></tr>
            )}
            {!isLoading && roles.map((r) => (
              <tr key={r.id} data-testid={`role-row-${r.id}`} className="hover:bg-white/[0.03]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/95">{r.name}</span>
                    {r.is_system_default && <Pill tone="gold"><Lock size={10} /> System</Pill>}
                  </div>
                  {r.description && (
                    <div className="text-xs text-white/55 mt-0.5">{r.description}</div>
                  )}
                </td>
                <td className="px-5 py-3 text-white/70">
                  {BASE_ROLE_LABELS[r.base_role_enum] || r.base_role_enum}
                </td>
                <td className="px-5 py-3 text-white/70 font-mono">
                  {r.capabilities.length} / 46
                </td>
                <td className="px-5 py-3 text-white/70 font-mono">
                  {r.assigned_user_count}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <RequirePermission cap="staff.clone_role">
                      <button
                        type="button"
                        onClick={() => setCloneTarget(r)}
                        data-testid={`role-row-clone-${r.id}`}
                        className="px-2.5 py-1 text-xs rounded-lg border border-white/10 hover:bg-white/5 text-white/80 inline-flex items-center gap-1"
                      >
                        <Copy size={12} /> Clone
                      </button>
                    </RequirePermission>
                    <RequirePermission cap="staff.edit_role">
                      <button
                        type="button"
                        disabled={r.is_system_default}
                        onClick={() => setEditTarget(r)}
                        data-testid={`role-row-edit-${r.id}`}
                        className="px-2.5 py-1 text-xs rounded-lg border border-white/10 hover:bg-white/5 text-white/80 inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    </RequirePermission>
                    <RequirePermission cap="staff.deactivate_role">
                      <button
                        type="button"
                        disabled={r.is_system_default}
                        onClick={() => setDeactivateTarget(r)}
                        data-testid={`role-row-deactivate-${r.id}`}
                        className="px-2.5 py-1 text-xs rounded-lg border border-red-500/30 text-red-300/80 hover:bg-red-500/10 inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ShieldOff size={12} /> Deactivate
                      </button>
                    </RequirePermission>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialogs */}
      <CloneDialog
        open={!!cloneTarget}
        source={cloneTarget}
        busy={cloneM.isPending}
        onClose={() => setCloneTarget(null)}
        onConfirm={(body) => cloneM.mutate({ roleId: cloneTarget.id, body })}
      />
      <DeactivateDialog
        open={!!deactivateTarget}
        role={deactivateTarget}
        busy={deactivateM.isPending}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => deactivateM.mutate(deactivateTarget.id)}
      />
      <EditDrawer
        open={!!editTarget}
        role={editTarget}
        catalog={catalog}
        allRoles={roles}
        busy={editM.isPending}
        onClose={() => setEditTarget(null)}
        onSave={(body) => editM.mutate({ roleId: editTarget.id, body })}
      />

      {/* PR-Audit-02B G-07 — base-role picker for "Create role" entry
          point. This is NOT a new builder/wizard: it just routes the
          user to the right system_default, then opens the existing
          CloneDialog. The clone endpoint produces the new custom role,
          which is then editable through the existing EditDrawer. */}
      {createPickerOpen ? (
        <div
          data-testid="create-role-picker"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        >
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#15151B] p-6">
            <h3 className="text-lg font-semibold text-white">Create a new role</h3>
            <p className="text-sm text-white/55 mt-1">
              Pick the base role to start from. Your new role inherits that
              role&apos;s default capabilities — you can then restrict (never
              expand) them in the editor.
            </p>
            <div className="mt-4 space-y-2">
              {systemDefaults.length === 0 ? (
                <div className="text-xs text-white/50 italic py-3" data-testid="create-role-no-defaults">
                  System defaults are not loaded yet. Refresh and try again.
                </div>
              ) : (
                systemDefaults.map((sd) => (
                  <button
                    key={sd.id}
                    type="button"
                    data-testid={`create-role-base-${sd.base_role_enum}`}
                    onClick={() => {
                      setCloneTarget(sd);
                      setCreatePickerOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-xl border border-white/10 bg-[#0E0E14] hover:bg-white/5 hover:border-gold/30 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-white">
                          {sd.name}
                        </div>
                        <div className="text-xs text-white/50 mt-0.5">
                          Base: {BASE_ROLE_LABELS[sd.base_role_enum] || sd.base_role_enum}
                          {' · '}{sd.capabilities.length} capabilities · system default
                        </div>
                      </div>
                      <Copy size={14} className="text-white/40" />
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-end mt-5">
              <button
                type="button"
                onClick={() => setCreatePickerOpen(false)}
                data-testid="create-role-picker-cancel"
                className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
